'use client'

import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import DesktopCalendar from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/Calendar'
import MobileCalendar from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/mobileCalendar'
import CloseIcon from '@mui/icons-material/Close'
import { alpha, Box, Button, Dialog, IconButton, Typography } from '@mui/material'

export interface HotelDateRange {
    from: Date | null
    to: Date | null
}

export interface HotelDateModifierProps {
    variant: 'mobile' | 'desktop'
    open: boolean
    localRange: HotelDateRange
    activeCheckIn: boolean
    activeCheckOut: boolean
    checkOutEnabled: boolean
    checkInText: string
    checkOutText: string
    /** Why the selection can't be applied, e.g. over the max stay. Empty when it is fine. */
    warningText: string
    isRtl: boolean
    title: string
    checkInLabel: string
    checkOutLabel: string
    applyLabel: string
    /** Rooms-and-guests control shown beside the dates. Only the property (SEO) page has one. */
    guestsField?: React.ReactNode
    guestsLabel?: string
    canApply: boolean
    minDate: Date
    onCheckInClick: () => void
    onCheckOutClick: () => void
    onDateClick: (dates: HotelDateRange) => void
    onClose: () => void
    onApply: () => void
}

// Day-cell sizing for the single-month mobile grid — the calendar renders a react-day-picker
// table, so the cells need explicit widths to fill the sheet.
const MOBILE_CALENDAR_SX = {
    flex: 1,
    overflowY: 'auto',
    '& .DayPicker': { width: '100%', margin: 0 },
    '& .DayPicker-wrapper': { pb: 0, outline: 'none' },
    '& .DayPicker-Month': { width: '100%', margin: 0, display: 'table', tableLayout: 'fixed' },
    '& .DayPicker-Body': { display: 'table-row-group' },
    '& .DayPicker-Week': { display: 'table-row' },
    '& .DayPicker-WeekdaysRow': { display: 'table-row' },
    '& .DayPicker-Day': {
        width: 'calc((100vw - 32px) / 7)',
        height: 'calc((100vw - 32px) / 7)',
        maxWidth: '50px',
        maxHeight: '50px',
        display: 'table-cell',
        verticalAlign: 'middle',
        textAlign: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation'
    },
    '& .DayPicker-Weekday': {
        width: 'calc((100vw - 32px) / 7)',
        maxWidth: '50px',
        display: 'table-cell',
        textAlign: 'center',
        padding: '8px 0',
        color: 'textColors.secondary',
        fontSize: '14px'
    },
    '& .DayPicker-Caption': { display: 'table-caption', marginBottom: '16px', textAlign: 'center' },
    '& .DayPicker-NavBar': { position: 'absolute', top: 0, left: 0, right: 0 }
} as const

// Stay-date picker for the details page. Opened from the header card's date row — it owns no
// trigger of its own. Layout mirrors the home-search picker (and RN's DateUpdateSheet): the
// calendar is inline and always visible, with the two legs and the action in a footer.
const HotelDateModifier: React.FC<HotelDateModifierProps> = ({
    variant,
    open,
    localRange,
    activeCheckIn,
    activeCheckOut,
    checkOutEnabled,
    checkInText,
    checkOutText,
    warningText,
    isRtl,
    title,
    checkInLabel,
    checkOutLabel,
    applyLabel,
    guestsField,
    guestsLabel,
    canApply,
    minDate,
    onCheckInClick,
    onCheckOutClick,
    onDateClick,
    onClose,
    onApply
}) => {
    const isDesktop = variant === 'desktop'

    const header = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                flexShrink: 0
            }}
        >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'textColors.primary' }}>{title}</Typography>
            <IconButton onClick={onClose} size="small" aria-label={title}>
                <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
        </Box>
    )

    const commonCalendarProps = {
        isRtl,
        selectedDates: localRange,
        roundTrip: true as const,
        onClickCalendar: onDateClick,
        activeOnward: activeCheckIn,
        activeReturn: activeCheckOut,
        isMulticity: false,
        multiCityDestinations: {},
        index: 1,
        minDate,
        fareCalendar: {},
        cheapestPricesByMonth: {},
        directionClick: () => {}
    }

    const calendar = isDesktop ? (
        <Box sx={{ px: 1, py: 1 }}>
            <DesktopCalendar {...commonCalendarProps} className="w-100p" title="" />
        </Box>
    ) : (
        <Box sx={MOBILE_CALENDAR_SX}>
            <MobileCalendar {...commonCalendarProps} numberOfMonths={1} />
        </Box>
    )

    const field = (label: string, control: React.ReactNode) => (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'textColors.secondary', mb: 0.5 }}>
                {label}
            </Typography>
            {/* Controls differ in natural height, so each fills the cell instead. */}
            <Box sx={{ flex: 1, display: 'flex', '& > *': { flex: 1 } }}>{control}</Box>
        </Box>
    )

    const leg = (label: string, value: string, active: boolean, enabled: boolean, onClick: () => void) =>
        field(
            label,
            <Box
                role="button"
                onClick={enabled ? onClick : undefined}
                sx={theme => ({
                    p: 1.5,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? alpha(theme.palette.primary.light, 0.2) : 'transparent',
                    cursor: enabled ? 'pointer' : 'not-allowed',
                    opacity: enabled ? 1 : 0.5
                })}
            >
                <Typography
                    sx={{ fontSize: 14, fontWeight: 600, color: 'textColors.primary', whiteSpace: 'nowrap' }}
                >
                    {value}
                </Typography>
            </Box>
        )

    // Same chrome as a date leg, so the fields read as one set.
    const guestsShell = guestsField && (
            <Box
                sx={{
                    // Matches a date leg's padding. Desktop stretches this cell to the row's
                    // height anyway; on mobile it stands alone, so it sets its own.
                    p: 1.5,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    // The control brings its own button padding; the shell owns spacing here
                    // so it lines up with the date legs beside it.
                    // The field itself is the affordance here, so the control inside it does
                    // not also light up on hover.
                    '& .MuiButtonBase-root': {
                        p: 0,
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        // Same line box as the date legs, so the three fields match in height.
                        lineHeight: 1.5,
                        // The field is the affordance; the control inside it stays flat in
                        // every state, including while its menu is open.
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' }
                    }
                }}
            >
                {guestsField}
            </Box>
    )

    const guests = guestsShell && field(guestsLabel ?? '', guestsShell)

    const footer = (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'neutral.white', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, mb: 1.5 }}>
                {leg(checkInLabel, checkInText, activeCheckIn, true, onCheckInClick)}
                {leg(checkOutLabel, checkOutText, activeCheckOut, checkOutEnabled, onCheckOutClick)}
                {isDesktop && guests}
            </Box>
            {!isDesktop && guests && <Box sx={{ mb: 2 }}>{guests}</Box>}
            {warningText && (
                <Typography sx={{ fontSize: 12, color: 'error.main', mb: 1.5, textAlign: 'center' }}>
                    {warningText}
                </Typography>
            )}
            <Button
                fullWidth
                variant="customVariantPrimary"
                size="large"
                disabled={!canApply}
                onClick={onApply}
                sx={{ py: 1.5, borderRadius: 1, textTransform: 'none', fontWeight: 600, fontSize: '1rem' }}
            >
                {applyLabel}
            </Button>
        </Box>
    )

    const body = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'neutral.white' }}>
            {header}
            {calendar}
            {footer}
        </Box>
    )

    // Width follows the two-month grid; a breakpoint maxWidth clips the second month.
    if (isDesktop) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                PaperProps={{ sx: { borderRadius: 2, width: 'fit-content', maxWidth: '96vw' } }}
            >
                {body}
            </Dialog>
        )
    }

    return (
        <SwipeableDrawerWithBackNavigation
            open={open}
            onOpen={() => {}}
            onClose={onClose}
            sx={{ height: '100dvh', maxHeight: '100dvh' }}
        >
            {body}
        </SwipeableDrawerWithBackNavigation>
    )
}

export { HotelDateModifier }
export default HotelDateModifier
